import {
    DurationMetricLabel,
    PudoTransactionTimersDurationByUserDropOffListChart,
    PudoTransactionTimersDurationByUserPickUpListChart,
    toDurationParts
} from '@frontend/chart/components';
import { formatTimeDifference } from '@frontend/common';
import { CommonMessage } from '@frontend/lang';
import { type ReactNode, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { InsightCardWidth } from '../insight-card-width';
import { InsightCard } from '../insight-card/insight-card.component';
import { InsightClientListPagination } from '../insight-client-list-pagination.component';
import { InsightId } from '../insight-registry/insight-ids';
import {
    PudoTransactionTimerDurationByUserItem,
    PudoTransactionTimerDurationUserKind,
    usePudoTransactionTimerDurationByUserList
} from './use-pudo-transaction-timer-duration-by-user-list';

const DETAIL_PAGE_SIZE = 25;

export interface PudoTransactionTimerDurationByUserRoleListProps {
    accountId: string;
    width?: InsightCardWidth;
    timerKind: PudoTransactionTimerDurationUserKind;
    insightId: string;
    chartKey: string;
    title: ReactNode;
    subtitle: ReactNode;
}

/** Exact clock duration for native hover title (`HH:mm` or `Nd HH:mm`). */
const formatExactDuration = (durationSeconds: number): string => {
    const end = Date.now();
    return formatTimeDifference(new Date(end - durationSeconds * 1000).toISOString(), new Date(end).toISOString());
};

const UserDurationRows = ({ items }: { items: PudoTransactionTimerDurationByUserItem[] }) => {
    const intl = useIntl();
    const avgDurationLabel = intl.formatMessage({
        id: 'platform.analytics.insights.pudo-transaction-timer-duration-by-user-list.avg-duration',
        description: 'Column header for average pending-timer duration on the PuDo slowest-users insights lists',
        defaultMessage: 'Avg duration'
    });

    return (
        <>
            <div className='border-border-default text-foreground-default flex items-start gap-4 border-b text-base font-medium'>
                <div className='w-full px-1 py-2'>{CommonMessage.OBJECTS.USER.DEFAULT}</div>
                <div className='w-[160px] shrink-0 py-2 text-right'>{avgDurationLabel}</div>
            </div>
            {items.map((item) => (
                <div
                    key={item.label}
                    className='border-border-default flex h-11 items-center gap-4 border-b'>
                    <div className='text-foreground-default w-full min-w-0 truncate px-1 text-sm font-medium'>{item.label}</div>
                    <div className='flex w-[160px] shrink-0 justify-end pe-1'>
                        <span
                            className='text-background-surface bg-brand-muted inline-flex min-w-8 cursor-default items-center justify-center rounded-full px-2 py-0.5 text-sm font-semibold'
                            title={formatExactDuration(item.durationSeconds)}>
                            <DurationMetricLabel {...toDurationParts(item.durationSeconds)} />
                        </span>
                    </div>
                </div>
            ))}
        </>
    );
};

export const PudoTransactionTimerDurationByUserRoleList = ({
    accountId,
    width,
    timerKind,
    insightId,
    chartKey,
    title,
    subtitle
}: PudoTransactionTimerDurationByUserRoleListProps) => {
    const { items, isLoading, hasData, isDetailMode } = usePudoTransactionTimerDurationByUserList(accountId, timerKind);
    const [pageIndex, setPageIndex] = useState(0);

    useEffect(() => {
        setPageIndex(0);
    }, [items]);

    const pageCount = Math.max(1, Math.ceil(items.length / DETAIL_PAGE_SIZE));
    const safePageIndex = Math.min(pageIndex, pageCount - 1);
    const pageItems = isDetailMode ? items.slice(safePageIndex * DETAIL_PAGE_SIZE, safePageIndex * DETAIL_PAGE_SIZE + DETAIL_PAGE_SIZE) : items;

    return (
        <InsightCard
            insightId={insightId}
            width={width}
            chartKey={chartKey}
            title={title}
            subtitle={subtitle}
            isLoading={isLoading}>
            {!hasData ? (
                <div className='text-foreground-default'>{CommonMessage.ERRORS.NO_DATA_AVAILABLE}</div>
            ) : isDetailMode ? (
                <div className='flex min-h-0 w-full min-w-0 flex-1 flex-col gap-4'>
                    <div className='min-h-0 w-full min-w-0 flex-1 overflow-y-auto'>
                        <UserDurationRows items={pageItems} />
                    </div>
                    <InsightClientListPagination
                        totalCount={items.length}
                        pageIndex={safePageIndex}
                        pageCount={pageCount}
                        onPageChange={setPageIndex}
                        navigationId='user-duration'
                    />
                </div>
            ) : (
                <div className='max-h-[420px] w-full min-w-0 overflow-y-auto'>
                    <UserDurationRows items={pageItems} />
                </div>
            )}
        </InsightCard>
    );
};

export interface PudoSlowestDropOffUsersListProps {
    accountId: string;
    width?: InsightCardWidth;
}

export const PudoSlowestDropOffUsersList = ({ accountId, width }: PudoSlowestDropOffUsersListProps) => (
    <PudoTransactionTimerDurationByUserRoleList
        accountId={accountId}
        width={width}
        timerKind='drop-off'
        insightId={InsightId.PUDO_SLOWEST_DROP_OFF_USERS}
        chartKey={PudoTransactionTimersDurationByUserDropOffListChart.definition.key}
        title={
            <FormattedMessage
                id='platform.analytics.insights.pudo-transaction-timer-duration-by-user-list.slowest-drop-off-title'
                description='Title for the PuDo slowest-senders list on analytics insights'
                defaultMessage='Slowest senders'
            />
        }
        subtitle={
            <FormattedMessage
                id='platform.analytics.insights.pudo-transaction-timer-duration-by-user-list.slowest-drop-off-subtitle'
                description='Subtitle indicating slowest senders covers the last 30 days on the PuDo analytics insights tab'
                defaultMessage='last 30 days'
            />
        }
    />
);

export interface PudoSlowestPickUpUsersListProps {
    accountId: string;
    width?: InsightCardWidth;
}

export const PudoSlowestPickUpUsersList = ({ accountId, width }: PudoSlowestPickUpUsersListProps) => (
    <PudoTransactionTimerDurationByUserRoleList
        accountId={accountId}
        width={width}
        timerKind='pick-up'
        insightId={InsightId.PUDO_SLOWEST_PICK_UP_USERS}
        chartKey={PudoTransactionTimersDurationByUserPickUpListChart.definition.key}
        title={
            <FormattedMessage
                id='platform.analytics.insights.pudo-transaction-timer-duration-by-user-list.slowest-pick-up-title'
                description='Title for the PuDo slowest-receivers list on analytics insights'
                defaultMessage='Slowest receivers'
            />
        }
        subtitle={
            <FormattedMessage
                id='platform.analytics.insights.pudo-transaction-timer-duration-by-user-list.slowest-pick-up-subtitle'
                description='Subtitle indicating slowest receivers covers the last 30 days on the PuDo analytics insights tab'
                defaultMessage='last 30 days'
            />
        }
    />
);
